import { useTranslate } from '@tolgee/react'
import { CarrierType, OrderStatus } from '@tom-ui/planning/app/api'
import { ContainerJourneyStatus } from '@tom-ui/planning/components/molecules/ContainerJourneyStatusChip'
import { useOrderNavigation } from '@tom-ui/planning/hooks/useOrderNavigation'
import { useHoldEnforcement } from '@tom-ui/planning/utils/hold-enforcement'
import { ButtonOption, OptionsButton } from '@tom-ui/ui'
import { useUtilsStore } from '@tom-ui/utils'
import { observer } from 'mobx-react-lite'
import { FC } from 'react'
import {
    ContainerTurnoverListTableColumnsDto,
    ContainerTurnoverListViewStore,
} from '../../stores/container-turnover-list.view-store'

type Props = {
  containerTurnover: ContainerTurnoverListTableColumnsDto
  listStore: ContainerTurnoverListViewStore
}

export const ContainerTurnoverListMenuOptions: FC<Props> = observer(
  ({ containerTurnover, listStore }) => {
    const { t } = useTranslate()
    const { openEditOrder, navigateJourneyOrderPage } = useOrderNavigation()
    const { containerMovementDialogStore } = useUtilsStore()

    const { isBlocking: isInternalMoveBlocked } = useHoldEnforcement(
      { containerJourneyId: containerTurnover.containerJourneyId },
      'InternalMove',
    )

    if (!containerTurnover.number) {
      return false
    }

    const actions: ButtonOption[] = [
      {
        label: t('viewDetails', 'View details'),
        testId: 'order-view-details-option',
        onClick: () => {
          const { carrierVisitType, carrierVisitId, id } = containerTurnover.originalOrder
          if (
            !carrierVisitId ||
            carrierVisitType === CarrierType.Truck ||
            carrierVisitType === CarrierType.Universal
          ) {
            navigateJourneyOrderPage({ orderId: id })
          } else {
            openEditOrder(containerTurnover.originalOrder)
          }
        },
      },
    ]

    if (
      containerTurnover.inboundStatus !== OrderStatus.Fulfilled ||
      containerTurnover.status === ContainerJourneyStatus.OnTerminal
    ) {
      actions.push({
        label: t('createReservation', 'Create reservation'),
        onClick: () =>
          listStore.toggleReservationDialog(
            containerTurnover.containerJourneyId!,
            containerTurnover.number,
          ),
      })
    }

    if (containerTurnover.status === ContainerJourneyStatus.OnYard) {
      actions.push(
        {
          label: t('updatePosition', 'Update position'),
          onClick: () =>
            listStore.toggleUpdatePositionDialog(
              containerTurnover.containerJourneyId!,
              containerTurnover.number,
            ),
        },
        {
          label: t('internalMove', 'Internal move'),
          disabled: isInternalMoveBlocked,
          onClick: () =>
            containerMovementDialogStore.openDialog([
              {
                containerJourneyId: containerTurnover.containerJourneyId!,
                containerNumber: containerTurnover.number!,
              },
            ]),
        },
      )
    }

    return <OptionsButton options={actions} position='right' testId='order-options-button' />
  },
)
